import React, { useState, useEffect, useCallback, useContext, useRef, useMemo } from 'react';
import { Typography, Button, Table, Select, Space, Input, Segmented, Popover, Checkbox, Tooltip, message } from 'antd';
import { PlusOutlined, SettingOutlined, HolderOutlined, CaretUpOutlined, CaretDownOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { UserName } from '../components/UserAvatar';
import { formatTS } from '../utils/time';
import HelpdeskCreateModal from '../components/HelpdeskCreateModal';
import ResizableTitle from '../components/ResizableTitle';
import useHelpdeskPulse from '../components/useHelpdeskPulse';
import { loadPref, savePref, readLocalPref } from '../utils/prefs';
import { UserContext } from '../App';
import { ticketKey } from '../components/HelpdeskInvolvedTickets';
import { HdStatusTag } from '../components/HelpdeskStatus';

let _cache = null;

// Every status not in the Done category (the server expands it).
const OPEN_STATUSES = ['@open'];

// Quick toggles above the filters: each sets its filter and searches at once.
// Status: the toggle (Open by default) unless statuses are picked in the
// dropdown, which then win; a toggle click clears the dropdown. Assigned: no
// toggle highlighted when the dropdown holds a person.
const STATUS_PRESETS = { open: OPEN_STATUSES, completed: ['completed'], all: [] };
const ASSIGNED_PRESETS = ['any', 'me', 'none', 'me_or_none'];

// Saved per user (utils/prefs: on the server, so it follows them to any
// computer, with a copy in this browser):
//   helpdesk.table        the table's layout, kept as it is changed:
//                         { columns: { order: [key], hidden: [key], widths: { key: px } },
//                           sort: { key, order } }
//   helpdesk.defaultView  the filters the page opens on, kept when "Save as my
//                         default" is clicked: { statusMode, status, queueIds, assignedTo }
// A saved view is only a filter: the server still shows only tickets the user
// can see.
// The Queue Options button's double chevron; it turns over while open.
const CHEVRONS = (
  <svg className="hd-qo-chev" viewBox="0 0 12 12" width="12" height="12" aria-hidden="true">
    <path d="M2.5 2.5 6 6l3.5-3.5M2.5 6.5 6 10l3.5-3.5" fill="none" stroke="currentColor" strokeWidth="1.5"
          strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const TABLE_PREF = 'helpdesk.table';
const VIEW_PREF = 'helpdesk.defaultView';
const NO_SORT = { key: null, order: null };
// The layout as this browser kept it before it was saved per user.
const oldLocal = (key) => {
  try { const v = JSON.parse(localStorage.getItem(key)); return v && typeof v === 'object' ? v : null; } catch { return null; }
};

// A header: the title, and the sort arrows as the only thing to click
// (ascending, descending, then unsorted), so the rest of the header is left
// for resizing. Drawn with antd's own sorter classes to look the same.
const NEXT_SORT = { ascend: 'descend', descend: null };
const SORT_HINT = { ascend: 'sort descending', descend: 'clear sort' };
function SortTitle({ title, order, onSort }) {
  return (
    <div className="ant-table-column-sorters">
      <span className="ant-table-column-title">{title}</span>
      <button
        type="button"
        className="hd-sort ant-table-column-sorter ant-table-column-sorter-full"
        aria-label={`${title}: ${SORT_HINT[order] ?? 'sort ascending'}`}
        title={`Click to ${SORT_HINT[order] ?? 'sort ascending'}`}
        onClick={onSort}
      >
        <span className="ant-table-column-sorter-inner" aria-hidden="true">
          <CaretUpOutlined className={`ant-table-column-sorter-up${order === 'ascend' ? ' active' : ''}`} />
          <CaretDownOutlined className={`ant-table-column-sorter-down${order === 'descend' ? ' active' : ''}`} />
        </span>
      </button>
    </div>
  );
}

// The Columns list: tick to show, drag to reorder. The last shown column
// cannot be hidden.
function ColumnPicker({ columns, order, hidden, onChange, onReset }) {
  const dragging = useRef(null);
  const title = Object.fromEntries(columns.map((c) => [c.key, c.title]));
  const shownCount = order.filter((k) => !hidden.has(k)).length;
  const moveOver = (k) => {
    const from = dragging.current;
    if (!from || from === k) return;
    const next = order.filter((x) => x !== from);
    next.splice(order.indexOf(k), 0, from);
    onChange({ order: next });
  };
  return (
    <div style={{ width: 220 }}>
      <div style={{ maxHeight: 420, overflowY: 'auto' }}>
        {order.map((k) => (
          <div
            key={k}
            draggable
            onDragStart={(e) => { dragging.current = k; e.dataTransfer.effectAllowed = 'move'; }}
            onDragOver={(e) => { e.preventDefault(); moveOver(k); }}
            onDragEnd={() => { dragging.current = null; }}
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', cursor: 'grab' }}
          >
            <HolderOutlined style={{ opacity: 0.45 }} />
            <Checkbox
              checked={!hidden.has(k)}
              disabled={!hidden.has(k) && shownCount === 1}
              onChange={(e) => onChange({ hidden: e.target.checked ? [...hidden].filter((x) => x !== k) : [...hidden, k] })}
            >
              {title[k]}
            </Checkbox>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
        <Button size="small" onClick={onReset}>Reset</Button>
      </div>
    </div>
  );
}

export default function HelpdeskPage() {
  const navigate = useNavigate();
  const currentUser = useContext(UserContext);
  // Anyone can raise a ticket for themselves; who else they can raise one
  // for comes from the server (CanEditHelpDesk).
  const canCreate = !!currentUser;

  const [rows,        setRows]        = useState(() => _cache?.rows ?? []);
  const [loading,     setLoading]     = useState(false);
  const [filterOpts,  setFilterOpts]  = useState({ queues: [], users: [], statuses: [] });
  const [statusMode,  setStatusMode]  = useState(() => _cache?.statusMode ?? 'open');
  const [status,      setStatus]      = useState(() => _cache?.status ?? []);
  const [queueIds,    setQueueIds]    = useState(() => _cache?.queueIds ?? []);
  const [assignedTo,  setAssignedTo]  = useState(() => _cache?.assignedTo ?? null);
  const [agentId,     setAgentId]     = useState(() => _cache?.agentId ?? null);
  const [q,           setQ]           = useState(() => _cache?.q ?? '');
  const [clientIds,   setClientIds]   = useState(() => _cache?.clientIds ?? []);
  const [contactIds,  setContactIds]  = useState(() => _cache?.contactIds ?? []);
  const [createOpen,  setCreateOpen]  = useState(false);
  const uid = currentUser?.ID;
  const [colPrefs,    setColPrefs]    = useState(() =>
    readLocalPref(uid, TABLE_PREF)?.columns ?? oldLocal('helpdesk-columns') ?? {});
  const [sort,        setSort]        = useState(() =>
    readLocalPref(uid, TABLE_PREF)?.sort ?? oldLocal('helpdesk-sort') ?? NO_SORT);

  // The layout: the server's copy replaces this browser's once it arrives;
  // after that every change is saved.
  const tableReady = useRef(false);
  useEffect(() => {
    loadPref(uid, TABLE_PREF).then((v) => {
      if (v) { setColPrefs(v.columns || {}); setSort(v.sort || NO_SORT); }
      tableReady.current = true;
      // A layout from before it was kept per user goes up to the server.
      if (!v && (oldLocal('helpdesk-columns') || oldLocal('helpdesk-sort'))) {
        savePref(uid, TABLE_PREF, { columns: oldLocal('helpdesk-columns') || {}, sort: oldLocal('helpdesk-sort') || NO_SORT });
      }
    });
  }, [uid]);
  useEffect(() => {
    if (tableReady.current) savePref(uid, TABLE_PREF, { columns: colPrefs, sort });
  }, [colPrefs, sort]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.get('/api/helpdesk/filter-options')
      .then((r) => setFilterOpts(r.data))
      .catch(() => {});
  }, []); // eslint-disable-line

  // Only the latest search's answer is shown, so a slow earlier one cannot
  // overwrite the list for filters that have since changed.
  const reqSeq = useRef(0);
  // The last search's filters, for the automatic refresh, and when it ran.
  const lastParams = useRef(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  // over: filter values to search with now (a quick toggle) before the
  // state holding them has updated.
  const load = useCallback(async (over = {}) => {
    const mode = over.statusMode ?? statusMode;
    const picked = over.status ?? status;
    const st = picked.length ? picked : STATUS_PRESETS[mode];
    const at = 'assignedTo' in over ? over.assignedTo : assignedTo;
    const qids = over.queueIds ?? queueIds;
    const seq = ++reqSeq.current;
    setLoading(true);
    try {
      const params = {};
      if (st.length)        params.status      = st.join(',');
      if (qids.length)      params.queue_ids   = qids.join(',');
      if (at)               params.assigned_to = at;
      if (agentId)          params.employee_user_id = agentId;   // impacted or applicable
      if (q.trim())         params.q           = q.trim();
      if (clientIds.length)  params.client_ids         = clientIds.join(',');
      if (contactIds.length) params.client_contact_ids = contactIds.join(',');
      lastParams.current = params;
      const res = await api.get('/api/helpdesk', { params });
      if (seq !== reqSeq.current) return;
      setRows(res.data);
      setUpdatedAt(new Date());
      _cache = { rows: res.data, statusMode: mode, status: picked, queueIds: qids, assignedTo: at, agentId, q, clientIds, contactIds };
    } catch {
      if (seq === reqSeq.current) message.error('Failed to load tickets.');
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
  }, [statusMode, status, queueIds, assignedTo, agentId, q, clientIds, contactIds]);

  // Searches whenever the page opens, with the filters last used (kept in
  // _cache), so coming back from a ticket shows its change -- or, on the first
  // visit, with the user's saved default view. A filter change waits for Search.
  useEffect(() => {
    if (_cache) { load(); return; }
    loadPref(uid, VIEW_PREF).then((v) => {
      if (!v) { load(); return; }
      const view = {
        statusMode: v.statusMode || 'open', status: v.status || [],
        queueIds: v.queueIds || [], assignedTo: v.assignedTo ?? null,
      };
      setStatusMode(view.statusMode); setStatus(view.status);
      setQueueIds(view.queueIds); setAssignedTo(view.assignedTo);
      load(view);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // The filters fold away behind Queue Options, and stay open until the
  // button is clicked again.
  const [optsOpen, setOptsOpen] = useState(false);

  const saveDefault = async () => {
    const onServer = await savePref(uid, VIEW_PREF, { statusMode, status, queueIds, assignedTo }, 0);
    if (onServer) message.success('Saved. Help Desk will open on this view.');
    else message.warning('Saved in this browser only: your account could not keep it right now.');
  };

  // When a ticket this user can see is raised or changed (assigned, status
  // ...), the list reloads with the last search's filters (not ones changed
  // since without Search), quietly: no spinner, no error message. How soon is
  // up to the queues the user works (useHelpdeskPulse). Rows that changed
  // flash briefly so the change is noticed.
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const [flash, setFlash] = useState(() => new Set());
  const flashTimer = useRef(0);
  useEffect(() => () => clearTimeout(flashTimer.current), []);
  useHelpdeskPulse('/api/helpdesk/pulse', async () => {
    if (!lastParams.current) return;
    const seq = ++reqSeq.current;
    try {
      const res = await api.get('/api/helpdesk', { params: lastParams.current });
      if (seq !== reqSeq.current) return;
      const before = new Map(rowsRef.current.map((r) => [r.ID, r]));
      const changed = new Set(res.data.filter((r) => {
        const b = before.get(r.ID);
        return !b || b.UpdatedAt !== r.UpdatedAt || b.AssignedToUserID !== r.AssignedToUserID || b.Status !== r.Status;
      }).map((r) => r.ID));
      setRows(res.data);
      setUpdatedAt(new Date());
      if (_cache) _cache = { ..._cache, rows: res.data };
      if (changed.size) {
        setFlash(changed);
        clearTimeout(flashTimer.current);
        flashTimer.current = setTimeout(() => setFlash(new Set()), 2500);
      }
    } catch { /* the next change tries again */ }
  });

  const strSort = (key) => (a, b) => (a[key] ?? '').localeCompare(b[key] ?? '');
  const numSort = (key) => (a, b) => (a[key] ?? 0) - (b[key] ?? 0);

  const columns = [
    { title: 'Ticket', dataIndex: 'ID', key: 'ID', width: 100, sorter: numSort('ID'),
      render: (v, r) => <Typography.Text strong style={{ color: '#1677ff' }}>{ticketKey(r)}</Typography.Text> },
    // Status second: it is what people scan the list for.
    { title: 'Status', dataIndex: 'Status', key: 'Status', width: 150, sorter: strSort('Status'),
      render: (v) => <HdStatusTag status={v} /> },
    { title: 'Subject', dataIndex: 'Subject', key: 'Subject', width: 240, sorter: strSort('Subject') },
    // Client, client contact and campaign sit under the Request Type rather
    // than in columns of their own; only the ones the ticket has.
    { title: 'Request Type', dataIndex: 'FormName', key: 'FormName', width: 200, sorter: strSort('FormName'),
      render: (v, r) => (
        <>
          <div>{v || <Typography.Text type="secondary">—</Typography.Text>}</div>
          {[['Client', r.ClientName], ['Contact', r.ClientContactName], ['Campaign', r.CampaignName]]
            .filter(([, val]) => val)
            .map(([label, val]) => (
              <div key={label} style={{ fontSize: 12 }}>
                <Typography.Text type="secondary">{label}: </Typography.Text>{val}
              </div>
            ))}
        </>
      ) },
    { title: 'Queue', dataIndex: 'QueueName', key: 'QueueName', width: 110, sorter: strSort('QueueName'),
      render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
    { title: 'Assigned To', dataIndex: 'AssignedToName', key: 'AssignedToName', width: 150, sorter: strSort('AssignedToName'),
      render: (v, r) => (v ? <UserName userId={r.AssignedToUserID} name={v} /> : <Typography.Text type="secondary">—</Typography.Text>) },
    // The person the ticket is about: the Applicable Employee on Request Types
    // that use one (there the Impacted Employee is just who raised it).
    { title: 'Impacted Employee', key: 'Employee', width: 170,
      sorter: (a, b) => (a.ApplicableName || a.AgentName || '').localeCompare(b.ApplicableName || b.AgentName || ''),
      render: (_, r) => (r.ApplicableName || r.AgentName
        ? <UserName userId={r.ApplicableName ? r.ApplicableUserID : r.AgentUserID} name={r.ApplicableName || r.AgentName} />
        : <Typography.Text type="secondary">—</Typography.Text>) },
    { title: 'Impacted Team', dataIndex: 'AgentTeamName', key: 'AgentTeamName', width: 130, sorter: strSort('AgentTeamName'),
      render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
    { title: 'Created By', dataIndex: 'CreatedByName', key: 'CreatedByName', width: 150, sorter: strSort('CreatedByName'),
      render: (v, r) => (v ? <UserName userId={r.CreatedByUserID} name={v} /> : <Typography.Text type="secondary">—</Typography.Text>) },
    { title: 'Due Date', dataIndex: 'DueDate', key: 'DueDate', width: 110, sorter: strSort('DueDate'),
      render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
    // Dates in the app's time zone (the stored values are UTC).
    { title: 'Created', dataIndex: 'CreatedAt', key: 'CreatedAt', width: 115, sorter: strSort('CreatedAt'),
      render: (v) => (v ? formatTS(v, 'YYYY-MM-DD') : '—') },
    { title: 'Last Change Date', dataIndex: 'UpdatedAt', key: 'UpdatedAt', width: 140, sorter: strSort('UpdatedAt'),
      render: (v) => (v ? formatTS(v, 'YYYY-MM-DD') : '—') },
    // Completed tickets only; a canceled ticket also has a closed date but
    // was not completed.
    { title: 'Completed Date', key: 'CompletedDate', width: 135,
      sorter: (a, b) => ((a.Status === 'completed' ? a.ClosedAt : '') || '').localeCompare((b.Status === 'completed' ? b.ClosedAt : '') || ''),
      render: (_, r) => (r.Status === 'completed' && r.ClosedAt ? formatTS(r.ClosedAt, 'YYYY-MM-DD') : '—') },
    // Also shown under the Request Type; kept last as columns to sort by.
    { title: 'Client', dataIndex: 'ClientName', key: 'ClientName', width: 150, sorter: strSort('ClientName'),
      render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
    { title: 'Client Contact', dataIndex: 'ClientContactName', key: 'ClientContactName', width: 150, sorter: strSort('ClientContactName'),
      render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
    { title: 'Campaign', dataIndex: 'CampaignName', key: 'CampaignName', width: 130, sorter: strSort('CampaignName'),
      render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
  ];

  // Saved order first (dropping columns that no longer exist), then any
  // columns added since, in their default place at the end.
  const byKey = Object.fromEntries(columns.map((c) => [c.key, c]));
  const savedOrder = (colPrefs.order || []).filter((k) => byKey[k]);
  const colOrder = [...savedOrder, ...columns.map((c) => c.key).filter((k) => !savedOrder.includes(k))];
  const hiddenCols = new Set(colPrefs.hidden || []);
  const shownColumns = colOrder.filter((k) => !hiddenCols.has(k)).map((k) => {
    const { sorter, ...col } = byKey[k];
    const width = colPrefs.widths?.[k] ?? col.width;
    const order = sort.key === k ? sort.order : null;
    return {
      ...col,
      title: (
        <SortTitle
          title={col.title}
          order={order}
          onSort={() => setSort({ key: k, order: order ? NEXT_SORT[order] : 'ascend' })}
        />
      ),
      width,
      onHeaderCell: () => ({
        width,
        'aria-sort': order ? `${order}ing` : undefined,
        onResize: (_, { size }) => setColPrefs((p) => ({ ...p, widths: { ...p.widths, [k]: size.width } })),
      }),
    };
  });
  const tableWidth = shownColumns.reduce((sum, c) => sum + c.width, 0);

  // Sorted here rather than by the table, whose sorting would make the whole
  // header the click target. Not by a hidden column. Only redone when the
  // rows or the sort change, not on every step of a resize drag.
  const sortedRows = useMemo(() => {
    const col = sort.order && !hiddenCols.has(sort.key) && byKey[sort.key];
    if (!col) return rows;
    const cmp = col.sorter;
    return [...rows].sort(sort.order === 'ascend' ? cmp : (a, b) => cmp(b, a));
  }, [rows, sort, colPrefs.hidden]); // eslint-disable-line react-hooks/exhaustive-deps

  const statusOptions  = (filterOpts.statuses || []).map((s) => ({ value: s.value, label: s.label }));
  const queueOptions   = (filterOpts.queues   || []).map((t) => ({ value: t.ID,    label: t.Name }));
  // Only people who can be or already are assigned a ticket, narrowed to the
  // chosen queue when there is one.
  const userOptions    = [
    { value: 'me', label: 'Me' },
    { value: 'none', label: 'Unassigned' },
    { value: 'me_or_none', label: 'Me + Unassigned' },
    ...(filterOpts.assignees || [])
      .filter((u) => !queueIds.length || (u.QueueIDs || []).some((qid) => queueIds.includes(qid)))
      .map((u) => ({ value: u.ID, label: u.DisplayName })),
  ];
  const pickStatus = (k) => { setStatusMode(k); setStatus([]); load({ statusMode: k, status: [] }); };
  const pickAssigned = (k) => { const at = k === 'any' ? null : k; setAssignedTo(at); load({ assignedTo: at }); };
  // Clients and contacts on tickets this user can see; contacts narrowed to
  // the chosen clients.
  const clientOptions  = (filterOpts.clients || []).map((c) => ({ value: c.ID, label: c.Name }));
  const contactOptions = (filterOpts.client_contacts || [])
    .filter((k) => !clientIds.length || clientIds.includes(k.ClientID))
    .map((k) => ({ value: k.ID, label: k.Name }));
  const employeeOptions = (filterOpts.employees || []).map((u) => ({
    value: u.ID, label: u.TeamName ? `${u.DisplayName} (${u.TeamName})` : u.DisplayName,
  }));

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>Help Desk</Typography.Title>
        {canCreate && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            New Ticket
          </Button>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <Button
          className={`hd-qo-btn${optsOpen ? ' hd-qo-on' : ''}`}
          aria-expanded={optsOpen}
          aria-controls="hd-queue-options"
          onClick={() => setOptsOpen((o) => !o)}
        >
          Queue Options {CHEVRONS}
        </Button>
        {updatedAt && (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Updates automatically · Updated {updatedAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', second: '2-digit' })}
          </Typography.Text>
        )}
        <div style={{ flex: 1 }} />
        <Popover
          trigger="click"
          placement="bottomRight"
          title="Columns"
          content={(
            <ColumnPicker
              columns={columns}
              order={colOrder}
              hidden={hiddenCols}
              onChange={(patch) => setColPrefs((p) => ({ ...p, ...patch }))}
              onReset={() => setColPrefs({})}
            />
          )}
        >
          <Button size="small" icon={<SettingOutlined />}>
            Columns{hiddenCols.size ? ` (${colOrder.length - hiddenCols.size} of ${colOrder.length})` : ''}
          </Button>
        </Popover>
      </div>

      {/* Unfolds below the button and pushes the table down; inert while
          folded so nothing in it can be tabbed to. */}
      <div id="hd-queue-options" className={`hd-qo${optsOpen ? ' hd-qo-open' : ''}`} inert={optsOpen ? undefined : ''}>
      <div className="hd-qo-inner">
      <div style={{ background: '#fff', borderRadius: 8, padding: '16px 16px 8px', marginBottom: 16, border: '1px solid #f0f0f0' }}>
        <Space wrap size={16} style={{ marginBottom: 12 }}>
          <Segmented
            value={status.length ? null : statusMode}
            onChange={pickStatus}
            options={[{ value: 'open', label: 'Open' }, { value: 'completed', label: 'Completed' }, { value: 'all', label: 'All' }]}
          />
          <Segmented
            value={ASSIGNED_PRESETS.includes(assignedTo ?? 'any') ? (assignedTo ?? 'any') : null}
            onChange={pickAssigned}
            options={[{ value: 'any', label: 'Anyone' }, { value: 'me', label: 'Assigned to Me' }, { value: 'none', label: 'Unassigned' },
                     { value: 'me_or_none', label: 'Me + Unassigned' }]}
          />
        </Space>
        <br />
        <Space wrap>
          <Select
            mode="multiple"
            allowClear
            maxTagCount="responsive"
            placeholder="Specific statuses"
            style={{ width: 420 }}
            options={statusOptions}
            value={status}
            onChange={setStatus}
          />
          <Select
            mode="multiple"
            allowClear
            showSearch
            optionFilterProp="label"
            maxTagCount="responsive"
            placeholder="Any queue"
            style={{ width: 260 }}
            // The list is as wide as the longest queue name, not the box.
            popupMatchSelectWidth={false}
            options={queueOptions}
            value={queueIds}
            onChange={setQueueIds}
          />
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Any assignee"
            style={{ minWidth: 180 }}
            options={userOptions}
            value={assignedTo}
            onChange={setAssignedTo}
          />
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Any impacted employee"
            style={{ minWidth: 220 }}
            options={employeeOptions}
            value={agentId}
            onChange={setAgentId}
          />
          {clientOptions.length > 0 && (
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              maxTagCount="responsive"
              placeholder="Any client"
              style={{ minWidth: 180 }}
              options={clientOptions}
              value={clientIds}
              onChange={(ids) => {
                setClientIds(ids);
                // Drop contacts that no longer belong to a chosen client.
                const keep = new Set((filterOpts.client_contacts || [])
                  .filter((k) => !ids.length || ids.includes(k.ClientID)).map((k) => k.ID));
                setContactIds((cur) => cur.filter((id) => keep.has(id)));
              }}
            />
          )}
          {clientOptions.length > 0 && (
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              maxTagCount="responsive"
              placeholder="Any client contact"
              style={{ minWidth: 180 }}
              options={contactOptions}
              value={contactIds}
              onChange={setContactIds}
            />
          )}
          <Input.Search
            placeholder="Search subject..."
            style={{ width: 220 }}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onSearch={() => load()}
            allowClear
          />
          <Button type="primary" onClick={() => load()} loading={loading}>Search</Button>
          <Tooltip title="Open Help Desk on these statuses, queues and assignee from now on, on any computer">
            <Button onClick={saveDefault}>Save as my default</Button>
          </Tooltip>
        </Space>
      </div>
      </div>
      </div>

      <Table
        rowKey="ID"
        columns={shownColumns}
        className="hd-cols"
        components={{ header: { cell: ResizableTitle } }}
        dataSource={sortedRows}
        loading={loading}
        size="small"
        // Fixed widths (dragged wider or narrower on the header edges),
        // scrolling sideways on narrow screens rather than squeezing them.
        tableLayout="fixed"
        scroll={{ x: tableWidth }}
        pagination={{ defaultPageSize: 50, showSizeChanger: true }}
        onRow={(r) => ({ onClick: () => navigate(`/helpdesk/${r.ID}`), style: { cursor: 'pointer' } })}
        rowClassName={(r) => (flash.has(r.ID) ? 'hd-flash' : '')}
      />

      <HelpdeskCreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        filterOpts={filterOpts}
      />
    </div>
  );
}
